import {
  EtiquetaDePagina,
  CLASE_TITULO_DE_PAGINA,
  CLASE_SUBTITULO_DE_PAGINA,
} from '@/components/patterns';
import { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { apiKeysApi } from '../services/api-keys-api';
import { ApiKey } from '../types/api-keys.types';
import {
  AVISO_IMPUESTOS,
  ejemplosCurlDeCarga,
  endpointsDeCarga,
  type EndpointDeCarga,
} from '../lib/endpoints-de-carga';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  AlertTriangle,
  Loader2,
  Terminal,
  RefreshCw,
  Play,
  CheckCircle2,
  XCircle,
  BookOpen,
  ChevronDown,
  KeyRound,
  Share2,
  Settings2,
  TestTube2,
} from 'lucide-react';
import { atributosSoporteNoPuede, useEnSoporte } from '@/features/mini-prixia/soporte-solo-mira';
import { copyToClipboard } from '@/lib/utils/clipboard';

export function ApiKeysPage() {
  const { toast } = useToast();
  // Soporte no crea ni revoca claves en NINGUNA empresa (`frenarEscriturasASoporte`).
  const enSoporte = useEnSoporte();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke dialog
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);

  // How to use guide
  const [showGuide, setShowGuide] = useState(true);

  // API Playground
  const [playgroundKey, setPlaygroundKey] = useState('');
  const [playgroundEndpoint, setPlaygroundEndpoint] = useState('products');
  const [playgroundBody, setPlaygroundBody] = useState('');
  const [playgroundResult, setPlaygroundResult] = useState<{
    success: boolean;
    data?: any;
    error?: string;
  } | null>(null);
  const [playgroundLoading, setPlaygroundLoading] = useState(false);

  // Plan 270: productos y stock por /api/v1; ventas y competencia no se ofrecen.
  const endpointConfig: Record<string, EndpointDeCarga> = endpointsDeCarga();

  const handlePlaygroundTest = async () => {
    if (!playgroundKey.trim()) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Ingresa tu API Key para probar.',
      });
      return;
    }
    let parsedBody: any;
    try {
      parsedBody = JSON.parse(playgroundBody);
    } catch {
      toast({
        variant: 'destructive',
        title: 'JSON inválido',
        description: 'Revisá el formato del cuerpo del request.',
      });
      return;
    }
    try {
      setPlaygroundLoading(true);
      setPlaygroundResult(null);
      const config = endpointConfig[playgroundEndpoint];
      const baseUrl = window.location.origin.replace(':3000', ':4000').replace(':5173', ':4000');
      // Ronda C (PROB-1) + ronda D (PROB-2): el probador de productos es un
      // simulacro — valida y responde sin escribir nada en el catálogo real de
      // la empresa. El de stock no tiene simulacro: escribe el stock que se
      // mande, así que la petición va sin ?revisar.
      const simulacro = playgroundEndpoint === 'products' ? '?revisar=true' : '';
      const resp = await fetch(`${baseUrl}${config.path}${simulacro}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': playgroundKey.trim() },
        body: JSON.stringify(parsedBody),
      });
      const json = await resp.json();
      setPlaygroundResult(json);
      if (json.success) fetchKeys();
    } catch (err: any) {
      setPlaygroundResult({ success: false, error: err.message || 'Error de conexión' });
    } finally {
      setPlaygroundLoading(false);
    }
  };

  // Set default body when endpoint changes
  useEffect(() => {
    setPlaygroundBody(endpointConfig[playgroundEndpoint].example);
    setPlaygroundResult(null);
  }, [playgroundEndpoint]);

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiKeysApi.getAll();
      setKeys(data);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudieron cargar las claves API.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    try {
      setCreating(true);
      const result = await apiKeysApi.create(newKeyName.trim());
      setCreatedKey(result.key);
      toast({
        title: 'API Key creada',
        description: 'Copia la clave ahora, no se mostrará de nuevo.',
      });
      fetchKeys();
    } catch (err) {
      // El motivo del servidor (por ejemplo, soporte que sólo mira: 403 SOPORTE_SOLO_MIRA), no un texto fijo.
      toast({
        variant: 'destructive',
        title: 'Error',
        description: mensajeDeError(err, 'No se pudo crear la API Key.'),
      });
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    await copyToClipboard(createdKey);
    setCopied(true);
    toast({ title: 'Copiada', description: 'API Key copiada al portapapeles.' });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setNewKeyName('');
    setCreatedKey(null);
    setCopied(false);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setRevoking(true);
      await apiKeysApi.revoke(revokeTarget.id);
      toast({
        title: 'API Key revocada',
        description: `La clave "${revokeTarget.name}" fue revocada exitosamente.`,
      });
      setRevokeOpen(false);
      setRevokeTarget(null);
      fetchKeys();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: mensajeDeError(err, 'No se pudo revocar la API Key.'),
      });
    } finally {
      setRevoking(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Nunca';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const curlExamples = ejemplosCurlDeCarga();

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="mb-3">
              <EtiquetaDePagina tono="neutro">Integraciones</EtiquetaDePagina>
            </div>
            <h1 className={CLASE_TITULO_DE_PAGINA}>
              <Key className="h-6 w-6" />
              Claves API
            </h1>
            <p className={`mt-2 ${CLASE_SUBTITULO_DE_PAGINA}`}>
              Gestiona las claves de acceso para integraciones externas con tu plataforma.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchKeys} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Dialog
              open={createOpen}
              onOpenChange={(open) => {
                if (!open) handleCloseCreate();
                else setCreateOpen(true);
              }}
            >
              <DialogTrigger asChild>
                <Button size="sm" {...atributosSoporteNoPuede(enSoporte)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear API Key
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear nueva API Key</DialogTitle>
                  <DialogDescription>
                    Ingresa un nombre descriptivo para identificar esta clave.
                  </DialogDescription>
                </DialogHeader>

                {!createdKey ? (
                  <>
                    <div className="space-y-2 py-4">
                      <Label htmlFor="key-name">Nombre</Label>
                      <Input
                        id="key-name"
                        placeholder="Ej: ERP Integración, Tienda Online..."
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                      />
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={handleCloseCreate}>
                        Cancelar
                      </Button>
                      <Button onClick={handleCreate} disabled={creating || !newKeyName.trim()}>
                        {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Crear
                      </Button>
                    </DialogFooter>
                  </>
                ) : (
                  <>
                    <div className="space-y-4 py-4">
                      <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">
                          Esta es la única vez que verás la clave completa. Cópiala y guárdala en un
                          lugar seguro.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input readOnly value={createdKey} className="font-mono text-sm" />
                        <Button variant="outline" size="icon" onClick={handleCopy}>
                          {copied ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleCloseCreate}>Cerrar</Button>
                    </DialogFooter>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* How to use guide */}
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="cursor-pointer pb-3" onClick={() => setShowGuide(!showGuide)}>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <BookOpen className="h-5 w-5 text-primary" />
                Cómo usar las claves API
              </CardTitle>
              <ChevronDown
                className={`h-5 w-5 text-muted-foreground transition-transform ${showGuide ? 'rotate-180' : ''}`}
              />
            </div>
            <CardDescription>
              Guia paso a paso para conectar sistemas externos con tu plataforma
            </CardDescription>
          </CardHeader>
          {showGuide && (
            <CardContent className="pt-0">
              <div className="grid gap-4 md:grid-cols-2">
                {/* Paso 1 */}
                <div className="flex gap-3 p-4 rounded-lg bg-background border">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold flex items-center gap-1.5">
                      <KeyRound className="h-4 w-4" />
                      Crear una API Key
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Clickea el botón <strong>"Crear API Key"</strong> arriba. Dale un nombre
                      descriptivo (ej: "ERP Producción", "Sistema Ventas"). Se te mostrara la clave
                      completa <strong>una sola vez</strong> — copiala y guardala en un lugar
                      seguro.
                    </p>
                  </div>
                </div>

                {/* Paso 2 */}
                <div className="flex gap-3 p-4 rounded-lg bg-background border">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold flex items-center gap-1.5">
                      <Share2 className="h-4 w-4" />
                      Compartir con tu equipo de IT
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Envia la API Key a tu equipo tecnico o proveedor de software. Ellos la usaran
                      para configurar el envío automático de datos desde tu ERP, sistema de ventas o
                      inventario.
                    </p>
                  </div>
                </div>

                {/* Paso 3 */}
                <div className="flex gap-3 p-4 rounded-lg bg-background border">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold flex items-center gap-1.5">
                      <Settings2 className="h-4 w-4" />
                      Configurar la integración
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      El sistema externo envia los datos por HTTP con el header
                      <code className="mx-1 px-1.5 py-0.5 bg-muted rounded text-xs">
                        X-API-Key: tu-clave
                      </code>
                      . Puede enviar <strong>productos e inventario</strong>. No necesita usuario ni
                      contraseña, solo la API Key.
                    </p>
                  </div>
                </div>

                {/* Paso 4 */}
                <div className="flex gap-3 p-4 rounded-lg bg-background border">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold flex items-center gap-1.5">
                      <TestTube2 className="h-4 w-4" />
                      Probar antes de integrar
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Usa la sección <strong>"Probar API"</strong> más abajo para verificar que todo
                      funciona antes de conectar el sistema real. También podés usar herramientas
                      como <strong>Postman</strong> para hacer pruebas.
                    </p>
                  </div>
                </div>
              </div>

              {/* Datos que se pueden enviar */}
              <div className="mt-4 p-4 rounded-lg bg-background border">
                <h4 className="font-semibold mb-3">Datos que podés enviar via API:</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 rounded-md bg-muted/50">
                    <div className="text-2xl mb-1">📦</div>
                    <p className="text-sm font-medium">Productos</p>
                    <p className="text-xs text-muted-foreground">
                      SKU, nombre, precio, costo, stock
                    </p>
                  </div>
                  <div className="text-center p-3 rounded-md bg-muted/50">
                    <div className="text-2xl mb-1">📊</div>
                    <p className="text-sm font-medium">Inventario</p>
                    <p className="text-xs text-muted-foreground">SKU, cantidad disponible</p>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Keys Table */}
        <Card>
          <CardHeader>
            <CardTitle>Claves activas</CardTitle>
            <CardDescription>Todas las claves API generadas para tu organización.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Cargando...
              </div>
            ) : keys.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Key className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p>Todavía no hay claves API.</p>
                <p className="text-sm mt-1">Crea una para comenzar a integrar.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-3 font-medium">Nombre</th>
                      <th className="pb-3 font-medium">Prefijo</th>
                      <th className="pb-3 font-medium">Estado</th>
                      <th className="pb-3 font-medium">Último uso</th>
                      <th className="pb-3 font-medium">Requests</th>
                      <th className="pb-3 font-medium text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((k) => (
                      <tr key={k.id} className="border-b last:border-0 hover:bg-muted/50">
                        <td className="py-3 font-medium">{k.name}</td>
                        <td className="py-3">
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {k.keyPrefix}...
                          </code>
                        </td>
                        <td className="py-3">
                          <Badge variant={k.isActive ? 'default' : 'secondary'}>
                            {k.isActive ? 'Activa' : 'Revocada'}
                          </Badge>
                        </td>
                        <td className="py-3 text-muted-foreground">{formatDate(k.lastUsedAt)}</td>
                        <td className="py-3 tabular-nums">{k.requestCount.toLocaleString()}</td>
                        <td className="py-3 text-right">
                          {k.isActive && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              {...atributosSoporteNoPuede(enSoporte)}
                              onClick={() => {
                                setRevokeTarget(k);
                                setRevokeOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Revocar
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revoke Confirmation Dialog */}
        <Dialog open={revokeOpen} onOpenChange={setRevokeOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revocar API Key</DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas revocar la clave "{revokeTarget?.name}"? Las
                integraciones que la usen dejarán de funcionar inmediatamente.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRevokeOpen(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleRevoke} disabled={revoking}>
                {revoking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Revocar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* API Playground */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Probar API
            </CardTitle>
            <CardDescription className="space-y-1">
              <span className="block">
                Probá los endpoints directamente desde aquí. Seleccioná el tipo de dato, pegá tu API
                Key, editá el JSON y enviá.
              </span>
              <span className="block">
                El probador de productos sólo revisa: no carga nada en tu catálogo. El de stock
                actualiza de verdad el stock de los SKU que mandes.
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* API Key input */}
            <div className="space-y-2">
              <Label>API Key</Label>
              <Input
                placeholder="pk_live_..."
                value={playgroundKey}
                onChange={(e) => setPlaygroundKey(e.target.value)}
                className="font-mono text-sm"
              />
            </div>

            {/* Endpoint selector */}
            <div className="space-y-2">
              <Label>Tipo de dato</Label>
              <Select value={playgroundEndpoint} onValueChange={setPlaygroundEndpoint}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="products">Productos</SelectItem>
                  <SelectItem value="stock">Inventario</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Endpoint:{' '}
                <code className="bg-muted px-1 rounded">
                  {endpointConfig[playgroundEndpoint].path}
                </code>
              </p>
            </div>

            {/* Body editor */}
            <div className="space-y-2">
              <Label>Cuerpo del request (JSON)</Label>
              <textarea
                className="w-full min-h-[160px] p-3 rounded-md border bg-muted font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                value={playgroundBody}
                onChange={(e) => setPlaygroundBody(e.target.value)}
                spellCheck={false}
              />
            </div>

            {/* Send button */}
            <Button onClick={handlePlaygroundTest} disabled={playgroundLoading}>
              {playgroundLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Enviar
            </Button>

            {/* Result */}
            {playgroundResult && (
              <div
                className={`p-4 rounded-lg border ${playgroundResult.success ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {playgroundResult.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span className="font-semibold text-sm">
                    {playgroundResult.success ? 'Exitoso' : 'Error'}
                  </span>
                </div>
                <pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto">
                  {JSON.stringify(playgroundResult, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Integration Guide */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Terminal className="h-5 w-5" />
              Guía de Integración
            </CardTitle>
            <CardDescription className="space-y-1">
              <span className="block">
                Usa tu API Key en el header{' '}
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">X-API-Key</code> para
                autenticar las llamadas a los endpoints de importación de datos.
              </span>
              <span className="block">{AVISO_IMPUESTOS}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {curlExamples.map((example) => (
              <div key={example.endpoint} className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold">{example.title}</h4>
                  <Badge variant="outline" className="font-mono text-xs">
                    {example.endpoint}
                  </Badge>
                </div>
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                  {example.curl}
                </pre>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

export default ApiKeysPage;
